// ─── Energy Utilities ─────────────────────────────────────────────────────────

export interface EnergyGrade {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}

/**
 * Returns an energy grade based on usage in kWh.
 * Adjust the thresholds according to the actual domain logic.
 */
export function getEnergyGrade(kwh: number | string): EnergyGrade {
  const value = typeof kwh === 'number' ? kwh : Number(kwh);

  // If the value is invalid or '-', return a default neutral state
  if (isNaN(value)) {
    return {
      label: 'energy.unknown',
      color: 'text-slate-400',
      bgColor: 'bg-slate-500/20',
      description: 'energy.desc_unknown',
    };
  }

  if (value < 400) {
    return {
      label: 'energy.saving',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20',
      description: 'energy.desc_saving',
    };
  }
  if (value < 800) {
    return {
      label: 'energy.normal',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/20',
      description: 'energy.desc_normal',
    };
  }
  if (value < 1200) {
    return {
      label: 'energy.high',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20',
      description: 'energy.desc_high',
    };
  }
  
  return {
    label: 'energy.excessive',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    description: 'energy.desc_excessive',
  };
}

/**
 * Converts Watt-hours (Wh) to kilo-Watt-hours (kWh).
 */
export function whToKwh(wh: number | string | undefined | null): number {
  if (wh === undefined || wh === null) return 0;
  const value = typeof wh === 'number' ? wh : Number(wh);
  return isNaN(value) ? 0 : Number((value / 1000));
}

/**
 * Format energy usage (input in kWh):
 * - If < 0.1 kWh: display in Wh (e.g. "75", unit "Wh")
 * - If >= 0.1 kWh: display in kWh with 2 decimal places (e.g. "1.25", unit "kWh")
 */
export function formatEnergyUsage(kwhValue: number | string | undefined | null): { value: string; unit: string; fullText: string; rawWh: number; isWh: boolean } {
  if (kwhValue === undefined || kwhValue === null) {
    return { value: '0', unit: 'Wh', fullText: '0 Wh', rawWh: 0, isWh: true };
  }
  const kwh = typeof kwhValue === 'number' ? kwhValue : Number(kwhValue);
  if (isNaN(kwh) || kwh <= 0) {
    return { value: '0', unit: 'Wh', fullText: '0 Wh', rawWh: 0, isWh: true };
  }
  if (kwh < 0.1) {
    const rawWh = kwh * 1000;
    const wh = Number(rawWh.toFixed(1));
    const whStr = wh % 1 === 0 ? String(Math.round(wh)) : wh.toFixed(1);
    return {
      value: whStr,
      unit: 'Wh',
      fullText: `${whStr} Wh`,
      rawWh,
      isWh: true
    };
  }
  const formattedKwh = kwh.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return {
    value: formattedKwh,
    unit: 'kWh',
    fullText: `${formattedKwh} kWh`,
    rawWh: kwh * 1000,
    isWh: false
  };
}

/**
 * Format energy usage from raw Wh input:
 * - If < 100 Wh: display in Wh
 * - If >= 100 Wh: display in kWh with 2 decimal places
 */
export function formatWhEnergy(whValue: number | string | undefined | null): { value: string; unit: string; fullText: string; rawWh: number; isWh: boolean } {
  if (whValue === undefined || whValue === null) {
    return { value: '0', unit: 'Wh', fullText: '0 Wh', rawWh: 0, isWh: true };
  }
  const wh = typeof whValue === 'number' ? whValue : Number(whValue);
  return formatEnergyUsage(wh / 1000);
}
